<template>
  <div class="audit-timeline" aria-live="polite">
    <div v-if="!items.length" class="audit-timeline__empty">
      <v-icon color="grey" class="mb-2">mdi-timeline-clock-outline</v-icon>
      <span>暂无审批记录</span>
    </div>
    <ol v-else class="audit-timeline__list">
      <li
        v-for="(item, index) in items"
        :key="item.id || item.taskId || `${item.activityName}-${index}`"
        class="audit-timeline__item"
      >
        <span
          class="audit-timeline__marker"
          :class="`audit-timeline__marker--${statusType(item.status)}`"
          aria-hidden="true"
        >
          <v-icon x-small color="white">{{ statusIcon(item.status) }}</v-icon>
        </span>
        <div class="audit-timeline__content">
          <div class="audit-timeline__title-row">
            <strong>{{ item.activityName || '审批环节' }}</strong>
            <v-chip
              v-if="showStatus && statusText(item.status)"
              x-small
              outlined
              :color="statusColor(item.status)"
            >
              {{ statusText(item.status) }}
            </v-chip>
          </div>
          <div v-if="roleNamesText(item)" class="audit-timeline__role">
            <v-icon x-small>mdi-account-key-outline</v-icon>
            <span>节点角色</span>
            <strong>{{ roleNamesText(item) }}</strong>
          </div>
          <div class="audit-timeline__meta">
            <span>
              <v-icon x-small>mdi-account-outline</v-icon>
              {{ item.assignee || '待处理' }}
            </span>
            <span v-if="item.endTime">
              <v-icon x-small>mdi-clock-outline</v-icon>
              {{ item.endTime }}
            </span>
          </div>
          <div v-if="item.duration" class="audit-timeline__duration">
            <v-icon x-small>mdi-timer-sand-complete</v-icon>
            <span>审批用时：{{ item.duration }}</span>
          </div>
          <p v-if="item.comment" class="audit-timeline__comment">
            {{ item.comment }}
          </p>
        </div>
      </li>
    </ol>
  </div>
</template>

<script>
export default {
  name: 'v-audit-timeline',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    statusMap: {
      type: Object,
      default: () => ({}),
    },
    showStatus: {
      type: Boolean,
      default: true,
    },
  },
  methods: {
    roleNamesText(item) {
      const roleNames = Array.isArray(item.roleNames)
        ? item.roleNames
        : String(item.roleNames || item.roleName || '')
            .split(',')
            .map((name) => name.trim())
      return roleNames.filter((name) => !!name).join('、')
    },
    statusText(status) {
      return this.statusMap[status] || ''
    },
    statusType(status) {
      if (status === 'success') return 'success'
      if (status === 'reject') return 'error'
      return 'pending'
    },
    statusColor(status) {
      if (status === 'success') return 'success'
      if (status === 'reject') return 'error'
      return 'secondary'
    },
    statusIcon(status) {
      if (status === 'success') return 'mdi-check'
      if (status === 'reject') return 'mdi-close'
      return 'mdi-clock-outline'
    },
  },
}
</script>

<style>
.audit-timeline {
  width: 100%;
}

.audit-timeline__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: var(--app-muted, #4b5a6a);
}

.audit-timeline__list {
  margin: 0;
  padding: 4px 0;
  list-style: none;
}

.audit-timeline__item {
  position: relative;
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 10px;
  padding: 0 0 16px;
}

.audit-timeline__item:not(:last-child)::after {
  position: absolute;
  top: 28px;
  bottom: 0;
  left: 13px;
  width: 1px;
  background: var(--app-border, rgba(15, 40, 70, 0.18));
  content: '';
}

.audit-timeline__marker {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--v-secondary-base, #1e3a5f);
}

.audit-timeline__marker--success {
  background: var(--v-success-base, #17b897);
}

.audit-timeline__marker--error {
  background: var(--v-error-base, #e05252);
}

.audit-timeline__content {
  min-width: 0;
  padding: 2px 0 0;
}

.audit-timeline__title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.audit-timeline__title-row strong {
  min-width: 0;
  color: var(--app-text, #0b1520);
  font-size: 0.9rem;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.audit-timeline__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-top: 4px;
  color: var(--app-muted, #4b5a6a);
  font-size: 0.78rem;
  line-height: 1.5;
}

.audit-timeline__role {
  display: grid;
  grid-template-columns: 16px auto minmax(0, 1fr);
  align-items: start;
  gap: 4px;
  margin-top: 5px;
  color: var(--app-muted, #4b5a6a);
  font-size: 0.78rem;
  line-height: 1.5;
}

.audit-timeline__role strong {
  color: var(--app-text, #0b1520);
  font-weight: 600;
  overflow-wrap: anywhere;
}

.audit-timeline__duration {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 3px 7px;
  color: var(--v-primary-base, #0b5c7a);
  font-size: 0.75rem;
  line-height: 1.45;
  background: rgba(11, 92, 122, 0.08);
  border-radius: 6px;
}

.audit-timeline__meta span {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.audit-timeline__comment {
  margin: 7px 0 0;
  padding: 7px 9px;
  color: var(--app-text, #0b1520);
  font-size: 0.82rem;
  line-height: 1.55;
  overflow-wrap: anywhere;
  background: var(--app-surface-2, #f4f7fb);
  border-radius: 8px;
}

@media print {
  .audit-timeline__item {
    break-inside: avoid;
  }
}
</style>
